"use client";

import { useEffect, useRef, useState } from "react";

import { parseJson5 } from "./json5";
// import type { BlockMatch } from '@llm-ui/react';
export type LLMOutputComponent<Props = unknown> = React.FC<
  Props & {
    blockMatch: BlockMatch;
  }
>;
export type LLMOutputFallbackBlock = {
  component: LLMOutputComponent;
};
export type BlockMatch = MatchBase & LLMOutputMatchWithLookBack;
export type MatchBase = {
  block: LLMOutputFallbackBlock;
  priority: number;
  isComplete: boolean;
};
export type LLMOutputMatchWithLookBack = LLMOutputMatch & {
  output: string;
  isVisible: boolean;
};
export type LLMOutputMatch = {
  startIndex: number;
  endIndex: number;
  outputRaw: string;
};

export type CustomBlock = {
  startChar: string;
  endChar: string;
  type: string;
  visibleIfPartial: boolean;
  component: React.ComponentType<{ blockMatch: BlockMatch }>;
};

export type FallbackComponent = React.ComponentType<{ blockMatch: BlockMatch }>;

export type UseLLMOutputParams = {
  llmOutput: string | unknown;
  blocks: CustomBlock[];
  fallbackComponent: FallbackComponent;
};

/**
 * Checks if the JSON content matches the expected type
 */
export function matchesType(
  jsonContent: string,
  expectedType: string,
  startChar: string,
  endChar: string
): boolean {
  // Remove startChar and endChar to get clean JSON
  const cleanJson = jsonContent
    .replace(new RegExp(`^${startChar.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`), "")
    .replace(new RegExp(`${endChar.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`), "");

  try {
    const parsed = parseJson5(cleanJson);
    if (typeof parsed === "object" && parsed !== null) {
      const typeValue = (parsed as { type?: string }).type;
      return typeValue === expectedType;
    }
  } catch {
    // If parsing fails, try to match type using regex for incomplete JSON
    const escapedType = expectedType.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const typePattern = new RegExp(`["']?type["']?\\s*[:=]\\s*["']?${escapedType}["']?`, "i");
    return typePattern.test(cleanJson);
  }

  return false;
}

/**
 * Finds all matches (complete and partial) for a given block in the LLM output
 * Filters matches by checking if the JSON content has a matching type field
 */
export function findBlockMatches(
  llmOutput: string | unknown,
  startChar: string,
  endChar: string,
  expectedType: string
): LLMOutputMatch[] {
  const matches: LLMOutputMatch[] = [];
  const openingIndices: number[] = [];
  let i = 0;

	if (typeof llmOutput !== "string") {
		return [];
	}

  while (i < llmOutput.length) {
    if (llmOutput.substring(i, i + startChar.length) === startChar) {
      openingIndices.push(i);
      i += startChar.length;
    } else if (
      openingIndices.length > 0 &&
      llmOutput.substring(i, i + endChar.length) === endChar
    ) {
      // Found closing char - create a complete match
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const startIndex = openingIndices.pop()!;
      const endIndex = i + endChar.length;
      const outputRaw = llmOutput.substring(startIndex, endIndex);

      // Only include match if type matches
      if (matchesType(outputRaw, expectedType, startChar, endChar)) {
        matches.push({
          startIndex,
          endIndex,
          outputRaw,
        });
      }
      i += endChar.length;
    } else {
      i++;
    }
  }

  // Add partial matches for unclosed blocks (from the last opening)
  if (openingIndices.length > 0) {
    const startIndex = openingIndices[openingIndices.length - 1];
    const outputRaw = llmOutput.substring(startIndex);

    // For partial matches, check if type matches (even if incomplete)
    if (matchesType(outputRaw, expectedType, startChar, endChar)) {
      matches.push({
        startIndex,
        endIndex: llmOutput.length,
        outputRaw,
      });
    }
  }

  return matches;
}

export interface GetMatchingBlocksParams {
  llmOutput: string | unknown;
  blocks: CustomBlock[];
  fallbackBlock: FallbackComponent;
}

export function getMatchingBlocks({
  llmOutput,
  blocks,
  fallbackComponent,
}: UseLLMOutputParams): BlockMatch[] {
  if (!llmOutput || typeof llmOutput !== "string") {
    return [];
  }

  const matches: BlockMatch[] = [];
  const blockMatchesByBlock: Array<{
    block: CustomBlock;
    match: LLMOutputMatch;
    priority: number;
  }> = [];

  // Find all block matches
  blocks.forEach((block, blockIndex) => {
    const foundMatches = findBlockMatches(llmOutput, block.startChar, block.endChar, block.type);

    foundMatches.forEach((match) => {
      // A block is complete if it contains the end delimiter
      const isComplete = match.outputRaw.includes(block.endChar);
      const shouldShow = isComplete || (!isComplete && block.visibleIfPartial);

      if (shouldShow) {
        blockMatchesByBlock.push({
          block,
          match,
          priority: blockIndex,
        });
      }
    });
  });

  // Sort matches by start index
  blockMatchesByBlock.sort((a, b) => a.match.startIndex - b.match.startIndex);

  // Process matches and create BlockMatch objects
  let lastProcessedIndex = 0;

  blockMatchesByBlock.forEach(({ block, match, priority }) => {
    // Add fallback text before this block if there's a gap
    if (match.startIndex > lastProcessedIndex) {
      const fallbackText = llmOutput.substring(lastProcessedIndex, match.startIndex);
      if (fallbackText.trim()) {
        matches.push({
          block: {
            component: fallbackComponent,
          } as BlockMatch["block"],
          priority: -1,
          isComplete: true,
          startIndex: lastProcessedIndex,
          endIndex: match.startIndex,
          outputRaw: fallbackText,
          output: fallbackText,
          isVisible: true,
        });
      }
    }

    // Add the block match
    // A block is complete if it contains the end delimiter
    const isComplete = match.outputRaw.includes(block.endChar);
    // Remove startChar and endChar from outputRaw to get the clean output
    const output = match.outputRaw
      .replace(new RegExp(`^${block.startChar.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`), "")
      .replace(new RegExp(`${block.endChar.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`), "");
    matches.push({
      block: {
        component: block.component,
      } as BlockMatch["block"],
      priority,
      isComplete,
      startIndex: match.startIndex,
      endIndex: match.endIndex,
      outputRaw: match.outputRaw,
      output: output,
      isVisible: isComplete || block.visibleIfPartial,
    });

    lastProcessedIndex = match.endIndex;
  });

  // Add remaining fallback text after the last block
  if (lastProcessedIndex < llmOutput.length) {
    const fallbackText = llmOutput.substring(lastProcessedIndex);
    if (fallbackText.trim()) {
      matches.push({
        block: {
          component: fallbackComponent,
        } as BlockMatch["block"],
        priority: -1,
        isComplete: true,
        startIndex: lastProcessedIndex,
        endIndex: llmOutput.length,
        outputRaw: fallbackText,
        output: fallbackText,
        isVisible: true,
      });
    }
  }

  // If no blocks were found, return the entire output as fallback
  if (matches.length === 0 && llmOutput.trim()) {
    matches.push({
      block: {
        component: fallbackComponent,
      } as BlockMatch["block"],
      priority: -1,
      isComplete: true,
      startIndex: 0,
      endIndex: llmOutput.length,
      outputRaw: llmOutput,
      output: llmOutput,
      isVisible: true,
    });
  }

  return matches;
}

/**
 * Custom hook to parse streaming LLM output and match blocks based on start/end characters
 */
export function useLLMOutput({
  llmOutput,
  blocks,
  fallbackComponent: fallbackBlock,
}: UseLLMOutputParams): { blockMatches: BlockMatch[] } {
  const [blockMatches, setBlockMatches] = useState<BlockMatch[]>([]);
  const blocksRef = useRef(blocks);
  const fallbackBlockRef = useRef(fallbackBlock);

  // Update refs when blocks/fallbackBlock change (without triggering rerender)
  blocksRef.current = blocks;
  fallbackBlockRef.current = fallbackBlock;

  useEffect(() => {
    const matches = getMatchingBlocks({
      llmOutput,
      blocks: blocksRef.current,
      fallbackComponent: fallbackBlockRef.current,
    });
    setBlockMatches(matches);
  }, [llmOutput]);

  return { blockMatches };
}

/**
 * Masque les blocs JSON 【 】 fermés et non fermés dans le texte.
 * Retourne uniquement le texte qui n'est pas dans des blocs.
 */
export function hideJsonBlocks(text: string): string {
  let result = "";
  let depth = 0;

  for (let i = 0; i < text.length; i++) {
    if (text[i] === "【") {
      depth++;
    } else if (text[i] === "】") {
      if (depth > 0) {
        depth--;
      } else {
        // Closing bracket without opening bracket - ignore it
        continue;
      }
    } else {
      // Only add character if we're not inside any block
      if (depth === 0) {
        result += text[i];
      }
    }
  }

  // Si un bloc reste ouvert, on a déjà ignoré tout le contenu depuis ce bloc
  // donc on retourne simplement le résultat construit (texte hors blocs)
  return result;
}
